import React, { useEffect, Suspense, lazy, useState } from 'react';
import './App.css';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import { CinematicHeader } from './components/CinematicHeader';
import { CinematicHero } from './components/CinematicHero';
import { CinematicStoneShowcase } from './components/CinematicStoneShowcase';
import { Analytics } from '@vercel/analytics/react';

// Lazy load below-the-fold components for better performance
const ImmersiveAbout = lazy(() => import('./components/ImmersiveAbout').then(m => ({ default: m.ImmersiveAbout })));
const CinematicServices = lazy(() => import('./components/CinematicServices').then(m => ({ default: m.CinematicServices })));
const CinematicProjects = lazy(() => import('./components/CinematicProjects').then(m => ({ default: m.CinematicProjects })));
const WhyChooseAndTestimonials = lazy(() => import('./components/WhyChooseAndTestimonials').then(m => ({ default: m.WhyChooseAndTestimonials })));
const CinematicContact = lazy(() => import('./components/CinematicContact').then(m => ({ default: m.CinematicContact })));
const CinematicFooter = lazy(() => import('./components/CinematicFooter').then(m => ({ default: m.CinematicFooter })));

const pageSections = [
  ImmersiveAbout,
  CinematicStoneShowcase,
  CinematicProjects,
  CinematicServices,
  WhyChooseAndTestimonials,
  CinematicContact,
  CinematicFooter,
];

// Simple loading skeleton
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#eef3f7]">
    <div className="animate-pulse text-slate-500">Loading...</div>
  </div>
);

function BackToTopButton() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToTop}
      aria-label="Back to top"
      className="fixed bottom-6 right-6 bg-slate-600 text-white p-3 rounded-full shadow-lg hover:bg-slate-500 transition-all z-40"
    >
      ↑
    </button>
  );
}

function App() {
  useEffect(() => {
    // Reduce animations on mobile for better performance
    const applyReducedMotionForMobile = () => {
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        document.documentElement.style.setProperty('--animation-duration', '0.1s');
        document.body.classList.add('reduced-motion');
      } else {
        document.body.classList.remove('reduced-motion');
      }
    };

    applyReducedMotionForMobile();
    window.addEventListener('resize', applyReducedMotionForMobile);

    return () => {
      window.removeEventListener('resize', applyReducedMotionForMobile);
    };
  }, []);

  return (
    <div className="App">
      <CinematicHeader />
      <main>
        <CinematicHero />
        {pageSections.map((SectionComponent, index) => (
          <ErrorBoundary key={index}>
            <Suspense fallback={<LoadingFallback />}>
              <SectionComponent />
            </Suspense>
          </ErrorBoundary>
        ))}
      </main>
      <BackToTopButton />
      <Toaster position="top-right" richColors />
      <Analytics />
    </div>
  );
}

export default App;
